import React, { useState } from 'react';
import { 
  View, 
  Text, 
  TextInput, 
  TouchableOpacity, 
  StyleSheet, 
  Alert, 
  KeyboardAvoidingView, 
  Platform,
  Image,
  SafeAreaView,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { RootStackParamList } from '../../types/navigation';
import { API_URL } from '../../config';
import { colors } from '../../theme/colors';
import { fonts } from '../../theme/fonts';

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const InvitationVerificationScreen: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const [email, setEmail] = useState('');
  const [secretPasscode, setSecretPasscode] = useState('');
  const [validationAnswer, setValidationAnswer] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleVerification = async () => {
    setError('');
    setSuccess(false);
    if (!email || !secretPasscode || !validationAnswer) {
      setError('Please fill in all fields.');
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/auth/verify-invitation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, secretPasscode, validationAnswer }),
      });
      const data = await response.json();
      if (!response.ok) {
        setError(data?.message || 'Verification failed.');
      } else {
        setSuccess(true);
        Alert.alert('Success', 'Invitation verified! You may now continue.', [
          { text: 'Continue', onPress: () => navigation.navigate('SignIn' as never) },
        ]);
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoid}
      >
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image 
            source={require('../../assets/icons/back.png')}
            style={styles.backIcon}
            resizeMode="contain"
          />
        </TouchableOpacity>

        <View style={styles.logoContainer}>
          <Image 
            source={require('../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <View style={styles.contentContainer}>
          <Text style={styles.title}>Verify Your Invitation</Text>
          <Text style={styles.subtitle}>Enter your details to verify your invitation</Text>

          <TextInput
            style={styles.input}
            placeholder="Email"
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
          />

          <TextInput
            style={styles.input}
            placeholder="Secret Passcode"
            value={secretPasscode}
            onChangeText={setSecretPasscode}
            secureTextEntry
          />

          <TextInput
            style={styles.input}
            placeholder="Validation Answer"
            value={validationAnswer}
            onChangeText={setValidationAnswer}
          />

          {error ? <Text style={styles.error}>{error}</Text> : null}
          {success ? <Text style={styles.success}>Invitation verified!</Text> : null}

          <TouchableOpacity
            style={styles.verifyButton}
            onPress={handleVerification}
            disabled={loading}
          >
            <Text style={styles.verifyButtonText}>
              {loading ? 'Verifying...' : 'Verify Invitation'}
            </Text>
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  keyboardAvoid: {
    flex: 1,
  },
  backButton: {
    position: 'absolute',
    top: ms(20),
    left: ms(20),
    zIndex: 1,
    padding: ms(10),
  },
  backIcon: {
    width: s(32),
    height: s(32),
  },
  logoContainer: {
    alignItems: 'center',
    marginTop: ms(60),
    marginBottom: ms(30),
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  contentContainer: {
    flex: 1,
    padding: ms(20),
    alignItems: 'center',
  },
  title: {
    fontSize: ms(28, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(12),
    textAlign: 'center',
  },
  subtitle: {
    fontSize: ms(16, 0.3),
    color: '#62656b',
    marginBottom: ms(32),
    textAlign: 'center',
  },
  input: {
    backgroundColor: '#FFFFFF',
    width: '100%',
    padding: ms(16),
    borderRadius: ms(12),
    marginBottom: ms(16),
    fontSize: ms(16, 0.3),
    borderWidth: ms(1),
    borderColor: '#E1E4E8',
  },
  verifyButton: {
    backgroundColor: '#29BFC0',
    paddingVertical: ms(16),
    paddingHorizontal: ms(32),
    borderRadius: ms(30),
    width: '100%',
    alignItems: 'center',
    marginTop: ms(16),
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  verifyButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  error: {
    color: colors.error,
    marginBottom: ms(8),
    textAlign: 'center',
  },
  success: {
    color: colors.success,
    marginBottom: ms(8),
    textAlign: 'center',
  },
});

export default InvitationVerificationScreen; 